/**
 * SRES · P28 Enterprise Workspace Panel
 * ═══════════════════════════════════════
 *
 * Composite panel exposing every Sprint P28 back-end service:
 *   · Item 4 · Resource Validation (swath / bias / slope of regression)
 *   · Item 5 · Calculated Classification (drill spacing · KE)
 *   · Item 6 · QA/QC (CRM / duplicates / blanks / reportability)
 *   · Item 7 · DEM ingest (XYZ CSV / GeoTIFF)
 *   · Item 8 · CPR Bundle Export (ZIP with SHA-256 manifest)
 *
 * Every result panel shows real numbers.  Never fake state.
 */
import { useRef, useState } from "react";
import { useQuery, useMutation, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import {
  Gauge, ChartBar, Ruler, ShieldCheck, Mountains, Package,
  Play, CheckCircle, Warning, ArrowClockwise, DownloadSimple,
  UploadSimple,
} from "@phosphor-icons/react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";


function errMsg(e, fb = "Request failed") {
  const d = e?.response?.data;
  if (typeof d === "string") return d;
  if (typeof d?.detail === "string") return d.detail;
  if (Array.isArray(d?.detail)) return d.detail.map((x) => x?.msg || JSON.stringify(x)).join(" · ");
  return e?.message || fb;
}


// ══════════════════════════════════════════════════════════════════════
// Item 4 · Resource Validation
// ══════════════════════════════════════════════════════════════════════
function ValidationSection({ pid }) {
  const qc = useQueryClient();
  const [bins, setBins] = useState(10);
  const q = useQuery({
    queryKey: ["p28-validation", pid],
    queryFn: () => api.get(`/projects/${pid}/p28/validation`).then((r) => r.data),
    enabled: !!pid,
  });
  const runMut = useMutation({
    mutationFn: () => api.post(`/projects/${pid}/p28/validation/run`,
      { n_bins: Number(bins) }).then((r) => r.data),
    onSuccess: () => { toast.success("Validation complete"); qc.invalidateQueries({ queryKey: ["p28-validation", pid] }); },
    onError: (e) => toast.error(errMsg(e)),
  });

  const d = q.data;
  const g = d?.global || {};
  const s = d?.slope  || {};
  const tone = d?.verdict === "ready" ? "emerald"
             : d?.verdict === "insufficient_data" ? "slate"
             : "amber";
  return (
    <div className="space-y-3" data-testid="p28-validation">
      <div className="flex items-center gap-2 flex-wrap">
        <Input type="number" min="3" max="40" value={bins}
                onChange={(e) => setBins(e.target.value)}
                className="h-8 w-20 text-[11px]"
                data-testid="p28-val-bins" />
        <span className="text-[10px] font-mono text-slate-500">swath bins</span>
        <Button size="sm" onClick={() => runMut.mutate()}
                 disabled={runMut.isPending}
                 className="h-8 text-[11px] bg-sky-600 hover:bg-sky-700 text-white"
                 data-testid="p28-val-run">
          <Play size={11} className="mr-1" />
          {runMut.isPending ? "Running…" : "Run validation"}
        </Button>
        {d?.generated_at && (
          <span className="ml-auto text-[10px] font-mono text-slate-500">
            last run · {new Date(d.generated_at).toLocaleString()}
          </span>
        )}
      </div>
      {!d?.generated_at ? (
        <div className="text-[11px] text-slate-500 italic py-3">
          NOT GENERATED — click Run validation.
        </div>
      ) : (
        <>
          <div className={`border rounded p-3 flex items-center gap-2 border-${tone}-300 bg-${tone}-50`}
               data-testid="p28-val-verdict">
            {d.verdict === "ready"
              ? <CheckCircle size={14} className={`text-${tone}-600`} weight="fill" />
              : <Warning size={14} className={`text-${tone}-600`} weight="fill" />}
            <div className="flex-1">
              <div className={`text-[12px] font-bold uppercase tracking-widest text-${tone}-800`}>
                {d.verdict.replace(/_/g, " ")}
              </div>
              <ul className={`text-[11px] text-${tone}-700 mt-0.5`}>
                {d.reasons?.map((r, i) => <li key={i}>· {r}</li>)}
              </ul>
            </div>
          </div>
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono">
            <div className="border border-slate-200 rounded p-2">
              <div className="text-[9px] text-slate-500 uppercase tracking-widest">Composites</div>
              <div className="text-slate-800 text-[13px] font-bold">{g.n_composites?.toLocaleString() ?? "—"}</div>
            </div>
            <div className="border border-slate-200 rounded p-2">
              <div className="text-[9px] text-slate-500 uppercase tracking-widest">Blocks</div>
              <div className="text-slate-800 text-[13px] font-bold">{g.n_blocks?.toLocaleString() ?? "—"}</div>
            </div>
            <div className="border border-slate-200 rounded p-2">
              <div className="text-[9px] text-slate-500 uppercase tracking-widest">Bias %</div>
              <div className={`text-[13px] font-bold ${
                    Math.abs(g.bias_percent || 0) > 10 ? "text-rose-700" : "text-emerald-700"}`}
                    data-testid="p28-val-bias">
                {g.bias_percent != null ? g.bias_percent.toFixed(2) : "—"}
              </div>
            </div>
            <div className="border border-slate-200 rounded p-2">
              <div className="text-[9px] text-slate-500 uppercase tracking-widest">Slope / R²</div>
              <div className="text-slate-800 text-[13px] font-bold"
                    data-testid="p28-val-slope">
                {s.slope?.toFixed(2) ?? "—"} · {s.r_squared?.toFixed(2) ?? "—"}
              </div>
            </div>
          </div>
          <div className="grid grid-cols-1 md:grid-cols-3 gap-2">
            {(d.swaths || []).map((sw) => (
              <SwathChart key={sw.axis} sw={sw} unit={g.composite_mean ? "g/t" : ""} />
            ))}
          </div>
        </>
      )}
    </div>
  );
}


function SwathChart({ sw, unit }) {
  const bins = sw.bins || [];
  if (!bins.length)
    return <div className="text-[10px] text-slate-500 italic p-3 border rounded">No {sw.axis} swath data.</div>;
  const w = 260, h = 120, pad = 22;
  const allVals = bins.flatMap((b) => [b.composite_mean, b.block_mean]).filter((v) => v != null);
  const vmax = Math.max(...allVals, 1e-6);
  const xStep = (w - 2 * pad) / bins.length;
  return (
    <div className="border border-slate-200 rounded p-2 bg-white"
         data-testid={`p28-val-swath-${sw.axis}`}>
      <div className="text-[10px] font-mono uppercase tracking-widest text-slate-500 mb-1">
        Swath · {sw.axis}
      </div>
      <svg viewBox={`0 0 ${w} ${h}`} className="w-full">
        <line x1={pad} x2={w - pad} y1={h - pad} y2={h - pad} stroke="#94a3b8" strokeWidth="0.5" />
        {bins.map((b, i) => {
          const x = pad + i * xStep;
          const yC = b.composite_mean != null ? h - pad - (b.composite_mean / vmax) * (h - 2 * pad) : null;
          const yB = b.block_mean    != null ? h - pad - (b.block_mean / vmax) * (h - 2 * pad) : null;
          const xN = pad + (i + 1) * xStep;
          const bC = bins[i + 1]?.composite_mean != null ? h - pad - (bins[i + 1].composite_mean / vmax) * (h - 2 * pad) : null;
          const bB = bins[i + 1]?.block_mean    != null ? h - pad - (bins[i + 1].block_mean / vmax) * (h - 2 * pad) : null;
          return (
            <g key={i}>
              {yC != null && bC != null && (
                <line x1={x} y1={yC} x2={xN} y2={bC} stroke="#dc2626" strokeWidth="1.5" />
              )}
              {yB != null && bB != null && (
                <line x1={x} y1={yB} x2={xN} y2={bB} stroke="#2563eb" strokeWidth="1.5" />
              )}
            </g>
          );
        })}
      </svg>
      <div className="flex items-center gap-3 text-[9px] font-mono text-slate-500">
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-rose-600 inline-block"></span> composites</span>
        <span className="flex items-center gap-1"><span className="w-2 h-2 bg-sky-600 inline-block"></span> blocks {unit}</span>
      </div>
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// Item 5 · Calculated Classification
// ══════════════════════════════════════════════════════════════════════
function ClassificationSection({ pid }) {
  const qc = useQueryClient();
  const [rules, setRules] = useState({
    Measured:  { spacing: 80,  min_samples: 8, min_holes: 3, min_ke: 0.6 },
    Indicated: { spacing: 200, min_samples: 4, min_holes: 2, min_ke: 0.3 },
    Inferred:  { spacing: 500, min_samples: 1, min_holes: 1, min_ke: 0.1 },
  });
  const q = useQuery({
    queryKey: ["p28-classification", pid],
    queryFn: () => api.get(`/projects/${pid}/p28/classification`).then((r) => r.data),
    enabled: !!pid,
  });
  const runMut = useMutation({
    mutationFn: () => api.post(`/projects/${pid}/p28/classification/run`,
      { rules }).then((r) => r.data),
    onSuccess: () => { toast.success("Classification complete"); qc.invalidateQueries({ queryKey: ["p28-classification", pid] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const d = q.data;
  const counts = d?.counts || {};
  const ore    = d?.ore_tonnes || {};
  const grade  = d?.ore_grade || {};
  const tone   = { Measured: "emerald", Indicated: "sky", Inferred: "amber", Unclassified: "slate" };

  const updateRule = (cls, field, value) =>
    setRules((r) => ({ ...r, [cls]: { ...r[cls], [field]: Number(value) } }));

  return (
    <div className="space-y-3" data-testid="p28-classification">
      <div className="border border-slate-200 rounded overflow-hidden">
        <div className="bg-slate-100 text-slate-600 text-[9px] font-mono uppercase tracking-widest px-2 py-1 grid grid-cols-6 gap-2">
          <div>Class</div>
          <div>Max spacing (m)</div>
          <div>Min samples</div>
          <div>Min holes</div>
          <div>Min KE</div>
          <div></div>
        </div>
        {["Measured", "Indicated", "Inferred"].map((cls) => (
          <div key={cls} className="grid grid-cols-6 gap-2 items-center px-2 py-1 border-t border-slate-100 text-[11px]">
            <Badge className={`bg-${tone[cls]}-100 text-${tone[cls]}-800 text-[10px] font-mono`}>{cls}</Badge>
            <Input type="number" step="10" value={rules[cls].spacing}
                    onChange={(e) => updateRule(cls, "spacing", e.target.value)}
                    className="h-7 text-[11px]" data-testid={`p28-cls-${cls}-spacing`} />
            <Input type="number" step="1" value={rules[cls].min_samples}
                    onChange={(e) => updateRule(cls, "min_samples", e.target.value)}
                    className="h-7 text-[11px]" data-testid={`p28-cls-${cls}-samples`} />
            <Input type="number" step="1" value={rules[cls].min_holes}
                    onChange={(e) => updateRule(cls, "min_holes", e.target.value)}
                    className="h-7 text-[11px]" data-testid={`p28-cls-${cls}-holes`} />
            <Input type="number" step="0.05" min="0" max="1" value={rules[cls].min_ke}
                    onChange={(e) => updateRule(cls, "min_ke", e.target.value)}
                    className="h-7 text-[11px]" data-testid={`p28-cls-${cls}-ke`} />
            <div></div>
          </div>
        ))}
      </div>
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => runMut.mutate()}
                 disabled={runMut.isPending}
                 className="h-8 text-[11px] bg-emerald-600 hover:bg-emerald-700 text-white"
                 data-testid="p28-cls-run">
          <Play size={11} className="mr-1" />
          {runMut.isPending ? "Classifying…" : "Run classification"}
        </Button>
        {d?.generated_at && (
          <span className="ml-auto text-[10px] font-mono text-slate-500">
            KE range · {d.kriging_range_used?.toFixed(0)} m ·
            last run · {new Date(d.generated_at).toLocaleString()}
          </span>
        )}
      </div>
      {d?.generated_at && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-2 text-[11px] font-mono">
          {["Measured", "Indicated", "Inferred", "Unclassified"].map((cls) => (
            <div key={cls} className={`border border-${tone[cls]}-200 rounded p-2 bg-${tone[cls]}-50`}
                  data-testid={`p28-cls-count-${cls}`}>
              <div className={`text-[9px] font-mono uppercase tracking-widest text-${tone[cls]}-700`}>{cls}</div>
              <div className={`text-[13px] font-bold text-${tone[cls]}-900`}>{counts[cls]?.toLocaleString() ?? 0} blocks</div>
              <div className={`text-[10px] text-${tone[cls]}-700`}>
                {(ore[cls] / 1e6 || 0).toFixed(2)} Mt @ {(grade[cls] || 0).toFixed(3)}
              </div>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// Item 6 · QA/QC
// ══════════════════════════════════════════════════════════════════════
function QAQCSection({ pid }) {
  const qc = useQueryClient();
  const q = useQuery({
    queryKey: ["p28-qaqc", pid],
    queryFn: () => api.get(`/projects/${pid}/p28/qaqc`).then((r) => r.data),
    enabled: !!pid,
  });
  const runMut = useMutation({
    mutationFn: () => api.post(`/projects/${pid}/p28/qaqc/run`, {}).then((r) => r.data),
    onSuccess: () => { toast.success("QA/QC run complete"); qc.invalidateQueries({ queryKey: ["p28-qaqc", pid] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const d = q.data;
  return (
    <div className="space-y-3" data-testid="p28-qaqc">
      <div className="flex items-center gap-2">
        <Button size="sm" onClick={() => runMut.mutate()}
                 disabled={runMut.isPending}
                 className="h-8 text-[11px] bg-indigo-600 hover:bg-indigo-700 text-white"
                 data-testid="p28-qaqc-run">
          <Play size={11} className="mr-1" />
          {runMut.isPending ? "Running…" : "Run QA/QC"}
        </Button>
        {d?.generated_at && (
          <span className="ml-auto text-[10px] font-mono text-slate-500">
            {d.n_crm} CRM · {d.n_duplicate} duplicates · {d.n_blank} blanks
          </span>
        )}
      </div>
      {!d?.generated_at ? (
        <div className="text-[11px] text-slate-500 italic py-3">
          NOT GENERATED — click Run QA/QC.
        </div>
      ) : (
        <>
          <div className={`border rounded p-3 flex items-center gap-2 ${
                d.reportable ? "border-emerald-300 bg-emerald-50" : "border-rose-300 bg-rose-50"}`}
               data-testid="p28-qaqc-verdict">
            {d.reportable
              ? <CheckCircle size={14} className="text-emerald-600" weight="fill" />
              : <Warning size={14} className="text-rose-600" weight="fill" />}
            <div>
              <div className={`text-[12px] font-bold uppercase tracking-widest ${
                    d.reportable ? "text-emerald-800" : "text-rose-800"}`}>
                {d.verdict.replace(/_/g, " ")}
              </div>
              <ul className={`text-[11px] mt-0.5 ${d.reportable ? "text-emerald-700" : "text-rose-700"}`}>
                {d.reasons?.map((r, i) => <li key={i}>· {r}</li>)}
              </ul>
            </div>
          </div>
          <div className="grid grid-cols-3 gap-2 text-[11px] font-mono">
            <div className="border border-slate-200 rounded p-2">
              <div className="text-[9px] text-slate-500 uppercase tracking-widest">CRM lots</div>
              <div className="text-slate-800 text-[13px] font-bold">{d.crm_charts?.length ?? 0}</div>
            </div>
            <div className="border border-slate-200 rounded p-2">
              <div className="text-[9px] text-slate-500 uppercase tracking-widest">Duplicate correlation</div>
              <div className="text-slate-800 text-[13px] font-bold">
                {d.duplicates?.correlation != null ? d.duplicates.correlation.toFixed(2) : "—"}
              </div>
            </div>
            <div className="border border-slate-200 rounded p-2">
              <div className="text-[9px] text-slate-500 uppercase tracking-widest">Blank contamination</div>
              <div className="text-slate-800 text-[13px] font-bold">
                {d.blanks?.contamination_pct != null ? d.blanks.contamination_pct.toFixed(1) + " %" : "—"}
              </div>
            </div>
          </div>
        </>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// Item 7 · DEM ingest
// ══════════════════════════════════════════════════════════════════════
function DEMSection({ pid }) {
  const qc = useQueryClient();
  const fileRef = useRef(null);
  const [subsample, setSubsample] = useState(10);

  const q = useQuery({
    queryKey: ["topography", pid],
    queryFn: () => api.get(`/projects/${pid}/dem/topography`).then((r) => r.data),
    enabled: !!pid,
  });
  const uploadMut = useMutation({
    mutationFn: async (file) => {
      const fd = new FormData();
      fd.append("file", file);
      return api.post(`/projects/${pid}/dem/ingest?subsample=${subsample}`, fd,
        { headers: { "Content-Type": "multipart/form-data" } }).then((r) => r.data);
    },
    onSuccess: (d) => { toast.success(`DEM ingested · ${d.n_vertices} vertices · ${d.n_faces} faces`); qc.invalidateQueries({ queryKey: ["topography", pid] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const deleteMut = useMutation({
    mutationFn: () => api.delete(`/projects/${pid}/dem/topography`).then((r) => r.data),
    onSuccess: () => { toast.success("Topography cleared"); qc.invalidateQueries({ queryKey: ["topography", pid] }); },
    onError: (e) => toast.error(errMsg(e)),
  });
  const d = q.data;

  return (
    <div className="space-y-3" data-testid="p28-dem">
      <div className="flex items-center gap-2 flex-wrap">
        <input ref={fileRef} type="file"
                accept=".csv,.xyz,.txt,.tif,.tiff,.geotiff"
                onChange={(e) => {
                  const f = e.target.files?.[0];
                  if (f) uploadMut.mutate(f);
                }}
                className="hidden"
                data-testid="p28-dem-file-input" />
        <Button size="sm" onClick={() => fileRef.current?.click()}
                 disabled={uploadMut.isPending}
                 className="h-8 text-[11px] bg-amber-600 hover:bg-amber-700 text-white"
                 data-testid="p28-dem-upload">
          <UploadSimple size={11} className="mr-1" />
          {uploadMut.isPending ? "Uploading…" : "Upload DEM (CSV / GeoTIFF)"}
        </Button>
        <div className="flex items-center gap-1">
          <span className="text-[10px] font-mono text-slate-500">GeoTIFF subsample</span>
          <Input type="number" min="1" max="200" value={subsample}
                  onChange={(e) => setSubsample(Number(e.target.value))}
                  className="h-8 w-16 text-[11px]"
                  data-testid="p28-dem-subsample" />
        </div>
        {d?.id && (
          <Button size="sm" variant="outline"
                   onClick={() => deleteMut.mutate()}
                   className="ml-auto h-8 text-[11px] text-rose-600 border-rose-200"
                   data-testid="p28-dem-delete">
            Clear topography
          </Button>
        )}
      </div>
      {!d?.id ? (
        <div className="border border-dashed border-slate-300 rounded p-6 text-center"
             data-testid="p28-dem-empty">
          <Mountains size={30} className="mx-auto text-slate-300" weight="duotone" />
          <div className="text-[12px] font-bold text-slate-500 mt-1">NOT GENERATED</div>
          <div className="text-[10px] text-slate-400">
            Upload a DEM CSV (x,y,z columns) or GeoTIFF to build a real topographic surface.
          </div>
        </div>
      ) : (
        <div className="border border-slate-200 rounded p-3 bg-white text-[11px] font-mono"
             data-testid="p28-dem-summary">
          <div className="grid grid-cols-2 md:grid-cols-4 gap-2">
            <div><span className="text-slate-500">Source · </span>{d.source}</div>
            <div><span className="text-slate-500">Vertices · </span>{d.n_vertices?.toLocaleString()}</div>
            <div><span className="text-slate-500">Faces · </span>{d.n_faces?.toLocaleString()}</div>
            <div><span className="text-slate-500">Subsample · </span>{d.subsample_factor}×</div>
            <div className="col-span-2"><span className="text-slate-500">X · </span>{d.bbox?.xmin?.toFixed(0)} → {d.bbox?.xmax?.toFixed(0)}</div>
            <div className="col-span-2"><span className="text-slate-500">Y · </span>{d.bbox?.ymin?.toFixed(0)} → {d.bbox?.ymax?.toFixed(0)}</div>
            <div className="col-span-2"><span className="text-slate-500">RL · </span>{d.bbox?.zmin?.toFixed(0)} → {d.bbox?.zmax?.toFixed(0)} m</div>
            <div className="col-span-2"><span className="text-slate-500">Uploaded · </span>{d.uploaded_at ? new Date(d.uploaded_at).toLocaleString() : "—"}</div>
          </div>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// Item 8 · CPR Bundle Export
// ══════════════════════════════════════════════════════════════════════
function BundleSection({ pid }) {
  const [runId, setRunId] = useState("");
  const runsQ = useQuery({
    queryKey: ["report-runs", pid],
    queryFn: () => api.get(`/projects/${pid}/report-runs`).then((r) => r.data),
    enabled: !!pid,
  });
  const runs = runsQ.data?.runs || runsQ.data?.items || [];

  const previewQ = useQuery({
    queryKey: ["bundle-manifest", pid, runId],
    queryFn: () => api.get(`/projects/${pid}/report-runs/${runId}/bundle-manifest`).then((r) => r.data),
    enabled: !!runId,
  });

  const download = async () => {
    if (!runId) return;
    try {
      const r = await api.get(`/projects/${pid}/report-runs/${runId}/bundle.zip`,
        { responseType: "blob" });
      const url = URL.createObjectURL(r.data);
      const a = document.createElement("a");
      a.href = url; a.download = `cpr_bundle_${runId.slice(0, 8)}.zip`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
      toast.success("Bundle downloaded");
    } catch (e) {
      toast.error(errMsg(e));
    }
  };

  const m = previewQ.data;

  return (
    <div className="space-y-3" data-testid="p28-bundle">
      <div className="flex items-center gap-2 flex-wrap">
        <select value={runId} onChange={(e) => setRunId(e.target.value)}
                 className="h-8 text-[11px] border border-slate-300 rounded px-1 min-w-[280px]"
                 data-testid="p28-bundle-run">
          <option value="">Select report run…</option>
          {runs.map((r) => (
            <option key={r.id} value={r.id}>
              {`${r.id} · ${r.status || "—"} · ${r.created_at ? new Date(r.created_at).toLocaleDateString() : "—"}`}
            </option>
          ))}
        </select>
        <Button size="sm" onClick={download} disabled={!runId}
                 className="h-8 text-[11px] bg-slate-800 hover:bg-slate-900 text-white"
                 data-testid="p28-bundle-download">
          <Package size={11} className="mr-1" /> Download bundle.zip
        </Button>
      </div>
      {previewQ.isFetching && <div className="text-[11px] text-slate-500 animate-pulse">Building manifest…</div>}
      {m && (
        <div className="border border-slate-200 rounded bg-white p-3" data-testid="p28-bundle-manifest">
          <div className="flex items-center gap-2 mb-2">
            <div className="text-[11px] font-bold text-slate-700">Bundle manifest</div>
            <Badge className="bg-slate-800 text-white text-[9px] font-mono">
              {m.artifact_count} artifacts
            </Badge>
            <div className="ml-auto text-[10px] font-mono text-slate-500">
              run · {m.report_run_id?.slice(0, 12)}
            </div>
          </div>
          <div className="max-h-64 overflow-y-auto">
            <div className="table-wrapper">
              <table className="w-full text-[10.5px] font-mono">
                <thead className="bg-slate-100 text-slate-500 sticky top-0">
                  <tr>
                    <th className="text-left px-2 py-1">Path</th>
                    <th className="text-right px-2 py-1">Bytes</th>
                    <th className="text-left px-2 py-1">SHA-256</th>
                  </tr>
                </thead>
                <tbody>
                  {m.artifacts.map((a) => (
                    <tr key={a.path} className="border-t border-slate-100">
                      <td className="px-2 py-0.5">{a.path}</td>
                      <td className="text-right px-2 py-0.5">{a.bytes.toLocaleString()}</td>
                      <td className="px-2 py-0.5 text-slate-400">{a.sha256.slice(0, 20)}…</td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          </div>
        </div>
      )}
    </div>
  );
}


// ══════════════════════════════════════════════════════════════════════
// Root panel with section tabs
// ══════════════════════════════════════════════════════════════════════
const TABS = [
  { key: "validation",     label: "Validation",     icon: Gauge },
  { key: "classification", label: "Classification", icon: ChartBar },
  { key: "qaqc",           label: "QA/QC",          icon: ShieldCheck },
  { key: "dem",            label: "DEM",            icon: Mountains },
  { key: "bundle",         label: "Bundle export",  icon: Package },
];


export default function P28WorkspacePanel({ projectId }) {
  const [tab, setTab] = useState("validation");
  const Icon = TABS.find((t) => t.key === tab)?.icon || Gauge;

  return (
    <div className="border border-slate-200 rounded-lg bg-white shadow-sm"
         data-testid="p28-workspace">
      <div className="bg-slate-900 text-slate-100 px-4 py-3 border-b border-slate-800 flex items-center gap-2 rounded-t-lg">
        <Icon size={16} className="text-sky-400" weight="duotone" />
        <h3 className="text-[14px] font-bold uppercase tracking-widest">
          P28 · Enterprise Workspace
        </h3>
        <Badge className="bg-sky-500 text-white text-[9px] font-mono">
          VALIDATION · CLASSIFICATION · QAQC · DEM · BUNDLE
        </Badge>
      </div>
      <div className="flex items-center gap-1 px-3 py-2 border-b border-slate-200 bg-slate-50">
        {TABS.map((t) => (
          <button key={t.key} onClick={() => setTab(t.key)}
                   className={`px-2 py-1 text-[10.5px] font-mono uppercase tracking-widest rounded transition ${
                     tab === t.key ? "bg-slate-900 text-white"
                                    : "text-slate-600 hover:bg-slate-200"}`}
                   data-testid={`p28-tab-${t.key}`}>
            <t.icon size={12} className="inline mr-1" /> {t.label}
          </button>
        ))}
      </div>
      <div className="p-4">
        {tab === "validation"     && <ValidationSection      pid={projectId} />}
        {tab === "classification" && <ClassificationSection  pid={projectId} />}
        {tab === "qaqc"           && <QAQCSection            pid={projectId} />}
        {tab === "dem"            && <DEMSection             pid={projectId} />}
        {tab === "bundle"         && <BundleSection          pid={projectId} />}
      </div>
    </div>
  );
}
